import React from 'react';
import axios from 'axios';

const WELCOME =
  "Hi! I'm the Nature's Nefer assistant. Ask me about our products, natural care tips, or the story behind the brand.";

const STARTER_PROMPTS = [
  'What products do you sell?',
  'How do I use H.O.T. Cocoa?',
  "What's your refund policy?",
  'Tell me about the brand',
];

class ChatAssistant extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      input: '',
      sending: false,
      error: null,
      messages: [{ role: 'assistant', content: WELCOME }],
    };
    this.messagesEndRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.open &&
      (prevState.messages !== this.state.messages ||
        prevState.open !== this.state.open)
    ) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  toggleOpen = () => {
    this.setState((prev) => ({ open: !prev.open, error: null }));
  };

  handleInputChange = (e) => {
    this.setState({ input: e.target.value });
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  sendMessage = async (presetText) => {
    const text = (
      typeof presetText === 'string' ? presetText : this.state.input
    ).trim();
    if (!text || this.state.sending) return;

    const userMessage = { role: 'user', content: text };
    const nextMessages = [...this.state.messages, userMessage];

    this.setState({
      messages: nextMessages,
      input: '',
      sending: true,
      error: null,
    });

    try {
      // Omit the static welcome so the model stays focused on real turns
      const apiMessages = nextMessages.filter(
        (m) => !(m.role === 'assistant' && m.content === WELCOME)
      );

      const { data } = await axios.post('/api/chat', {
        messages: apiMessages,
      });

      this.setState((prev) => ({
        messages: [
          ...prev.messages,
          { role: 'assistant', content: data.reply },
        ],
        sending: false,
      }));
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        'Something went wrong. Please try again.';
      this.setState({ sending: false, error: message });
    }
  };

  render() {
    const { open, input, sending, error, messages } = this.state;
    const showStarters =
      !sending &&
      messages.length === 1 &&
      messages[0].role === 'assistant' &&
      messages[0].content === WELCOME;

    return (
      <div className="chat-assistant">
        {open && (
          <div
            className="chat-assistant-panel"
            role="dialog"
            aria-label="Customer service chat"
          >
            <div className="chat-assistant-header">
              <div>
                <p className="chat-assistant-title">Nature's Nefer</p>
                <p className="chat-assistant-subtitle">Customer care</p>
              </div>
              <button
                type="button"
                className="chat-assistant-close"
                onClick={this.toggleOpen}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>

            <div className="chat-assistant-messages">
              {messages.map((m, i) => (
                <div
                  key={`${m.role}-${i}`}
                  className={`chat-bubble chat-bubble-${m.role}`}
                >
                  {m.content}
                </div>
              ))}
              {showStarters && (
                <div
                  className="chat-starter-prompts"
                  role="group"
                  aria-label="Suggested questions"
                >
                  {STARTER_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="chat-starter-prompt"
                      onClick={() => this.sendMessage(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
              {sending && (
                <div className="chat-bubble chat-bubble-assistant chat-bubble-typing">
                  Thinking…
                </div>
              )}
              {error && <p className="chat-assistant-error">{error}</p>}
              <div ref={this.messagesEndRef} />
            </div>

            <div className="chat-assistant-composer">
              <textarea
                className="chat-assistant-input"
                rows={2}
                value={input}
                onChange={this.handleInputChange}
                onKeyDown={this.handleKeyDown}
                placeholder="Ask about products or the brand…"
                disabled={sending}
                aria-label="Message"
              />
              <button
                type="button"
                className="chat-assistant-send"
                onClick={() => this.sendMessage()}
                disabled={sending || !input.trim()}
              >
                Send
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          className={`chat-assistant-fab${open ? ' is-open' : ''}`}
          onClick={this.toggleOpen}
          aria-expanded={open}
          aria-label={open ? 'Close chat assistant' : 'Open chat assistant'}
        >
          {open ? (
            'Close'
          ) : (
            <span className="chat-assistant-fab-label">
              <span className="chat-assistant-fab-icon" aria-hidden="true">
                <svg
                  className="chat-smile-icon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="1.75"
                  />
                  <circle cx="9" cy="10" r="1.25" fill="currentColor" />
                  <circle cx="15" cy="10" r="1.25" fill="currentColor" />
                  <path
                    d="M8.5 14.25c1.1 1.4 2.5 2.1 3.5 2.1s2.4-.7 3.5-2.1"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
                <svg
                  className="chat-wave-hand"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.2 13.2V7.4a1.15 1.15 0 0 1 2.3 0v4.2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10.5 11.6V5.8a1.15 1.15 0 0 1 2.3 0v5.4"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12.8 11V6.6a1.15 1.15 0 1 1 2.3 0V12"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M15.1 12.2V8.8a1.15 1.15 0 1 1 2.3 0v5.6c0 3.1-2.2 5.2-5.4 5.2-2.4 0-4.3-1.2-5.2-3.2L5.2 12a1.35 1.35 0 0 1 2.3-1.4l.7 1.2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Chat
            </span>
          )}
        </button>
      </div>
    );
  }
}

export default ChatAssistant;
